/**
 * Manipulation des jeux de règles côté interface.
 *
 * Le serveur revalide et renormalise tout ce qui lui arrive : ces fonctions ne
 * sont pas une barrière de sécurité, elles servent à ce que l'écran montre
 * immédiatement le barème tel qu'il sera enregistré, sans attendre un
 * aller-retour.
 */

import type { Channel, MarginTier, PricingChannel, RuleSet } from '@/api/types'

export const PRICING_CHANNELS: PricingChannel[] = ['amazon_heavy', 'amazon_light', 'darty', 'youkapi']

export const PRICING_CHANNEL_LABELS: Record<PricingChannel, { label: string; hint: string; color: string }> = {
  amazon_heavy: {
    label: 'Amazon — colis lourd',
    hint: 'Commission 7 %, appliquée au-delà du seuil de poids.',
    color: 'var(--amazon)',
  },
  amazon_light: {
    label: 'Amazon — colis standard',
    hint: 'Commission 15 %, appliquée jusqu’au seuil de poids.',
    color: 'var(--amazon)',
  },
  darty: { label: 'Darty', hint: 'Marketplace Darty. Colis lourd : toujours au-dessus du prix Amazon.', color: 'var(--darty)' },
  youkapi: {
    label: 'Youkapi.com',
    hint: 'Boutique PrestaShop, commission Stripe, transport facturé à part.',
    color: 'var(--youkapi)',
  },
}

export const FILE_LABELS: Record<Channel, { name: string; description: string; color: string }> = {
  amazon: { name: 'Amazon', description: 'Add Products (.xlsm)', color: 'var(--amazon)' },
  mirakl: { name: 'Mirakl', description: 'Fichier d’offres (.xlsx)', color: 'var(--mirakl)' },
  darty: { name: 'Darty', description: 'Fichier stock marketplace (.csv)', color: 'var(--darty)' },
  youkapi: { name: 'Youkapi.com', description: 'Import produits PrestaShop (.xlsx)', color: 'var(--youkapi)' },
}

export function clone<T>(value: T): T {
  return structuredClone(value)
}

/**
 * Classe les tranches par borne croissante et force la dernière à couvrir
 * « tout le reste ».
 *
 * Le calcul de prix retient la première tranche dont la borne dépasse le prix
 * d'achat : sans tranche ouverte en fin de liste, un produit plus cher que
 * toutes les bornes ne serait couvert par aucune.
 */
export function normalizeMargins(tiers: MarginTier[]): MarginTier[] {
  if (tiers.length === 0) return [{ upper: null, margin: 0 }]
  const sorted = [...tiers].sort((a, b) => (a.upper ?? Infinity) - (b.upper ?? Infinity))
  const last = sorted[sorted.length - 1]!
  sorted[sorted.length - 1] = { upper: null, margin: last.margin }
  return sorted
}

/** Ajoute une tranche juste avant « tout le reste ». */
export function addMarginTier(tiers: MarginTier[]): MarginTier[] {
  const bounded = tiers.filter((tier) => tier.upper !== null)
  const lastBound = bounded.length ? (bounded[bounded.length - 1]!.upper ?? 0) : 300
  const open = tiers[tiers.length - 1]
  const next = [...tiers]
  next.splice(next.length - 1, 0, { upper: Math.round(lastBound) + 200, margin: open?.margin ?? 0 })
  return normalizeMargins(next)
}

export function removeMarginTier(tiers: MarginTier[], index: number): MarginTier[] {
  if (tiers.length <= 1) return tiers
  return normalizeMargins(tiers.filter((_, position) => position !== index))
}

/** Libellé lisible d'une tranche : « 400 – 700 € », « ≥ 1 100 € »… */
export function describeTier(tiers: MarginTier[], index: number): string {
  const previous = index > 0 ? tiers[index - 1]!.upper : null
  const current = tiers[index]!.upper
  const money = (value: number) => value.toLocaleString('fr-FR')
  if (current === null) return previous === null ? 'tous les prix' : `≥ ${money(previous)} €`
  if (previous === null) return `< ${money(current)} €`
  return `${money(previous)} – ${money(current)} €`
}

/** Deux jeux de règles sont-ils identiques ? Sert à détecter les modifications non enregistrées. */
export function rulesEqual(a: RuleSet, b: RuleSet): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Applique le modèle de meta description comme le fera le serveur.
 * Utilisé pour l'aperçu en direct de l'écran des règles.
 */
export function renderMetaDescription(template: string, name: string, suffix: string): string {
  return template.replaceAll('{name}', name).replaceAll('{suffix}', suffix)
}

export const PREVIEW_PRODUCT_NAME = 'GSN36VICP Bosch surgélateur'
